import {
  makeStyles,
  tokens,
  Card,
  CardHeader,
  Text,
  Button,
  Spinner,
} from '@fluentui/react-components';
import { PersonRegular } from '@fluentui/react-icons';
import { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useVersionCheck } from '../hooks/useVersionCheck';
import { APP_NAME } from '../config/constants';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    backgroundColor: '#f3f2f1',
    backgroundImage: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  },
  card: {
    width: '400px',
    padding: tokens.spacingVerticalXXL,
    textAlign: 'center',
  },
  logo: {
    width: '80px',
    height: '80px',
    backgroundColor: '#0078d4',
    borderRadius: tokens.borderRadiusXLarge,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto',
    marginBottom: tokens.spacingVerticalL,
    fontSize: '32px',
    fontWeight: 'bold',
    color: 'white',
  },
  title: {
    fontSize: tokens.fontSizeBase600,
    fontWeight: tokens.fontWeightSemibold,
    marginBottom: tokens.spacingVerticalS,
    display: 'block',
  },
  subtitle: {
    color: tokens.colorNeutralForeground3,
    marginBottom: tokens.spacingVerticalXXL,
    display: 'block',
  },
  loginButton: {
    width: '100%',
    height: '48px',
    fontSize: tokens.fontSizeBase400,
    marginBottom: tokens.spacingVerticalM,
  },
  footer: {
    color: tokens.colorNeutralForeground4,
    fontSize: tokens.fontSizeBase200,
    marginTop: tokens.spacingVerticalXXL,
  },
  error: {
    color: tokens.colorPaletteRedForeground1,
    marginBottom: tokens.spacingVerticalM,
    display: 'block',
  },
});

export function LoginPage() {
  const styles = useStyles();
  const { login, isLoading } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [isSigningIn, setIsSigningIn] = useState(false);

  const { updateAvailable, latestVersion } = useVersionCheck();
  useEffect(() => {
    if (!updateAvailable || !latestVersion) return;
    const key = 'hts.autoReloadedFor';
    try {
      if (sessionStorage.getItem(key) === latestVersion) return;
      sessionStorage.setItem(key, latestVersion);
    } catch {
      // sessionStorage unavailable: still reload once per page load
    }
    window.location.reload();
  }, [updateAvailable, latestVersion]);

  const handleLogin = async () => {
    setError(null);
    setIsSigningIn(true);
    try {
      await login();
    } catch (err) {
      // Only reset on failure: on success the page navigates away to Microsoft.
      setError('Sign in failed. Please try again.');
      console.error('Login error:', err);
      setIsSigningIn(false);
    }
  };

  if (isLoading) {
    return (
      <div className={styles.container}>
        <Spinner size="large" label="Loading..." />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <Card className={styles.card}>
        <CardHeader
          header={
            <>
              <div className={styles.logo}>HR</div>
              <Text className={styles.title}>{APP_NAME}</Text>
              <Text className={styles.subtitle}>
                Streamline your employee onboarding and offboarding process
              </Text>
            </>
          }
        />

        {error && <Text className={styles.error}>{error}</Text>}

        <Button
          appearance="primary"
          className={styles.loginButton}
          icon={isSigningIn ? <Spinner size="tiny" /> : <PersonRegular />}
          onClick={handleLogin}
          disabled={isSigningIn}
        >
          {isSigningIn ? 'Signing in...' : 'Sign in with Microsoft'}
        </Button>

        <Text className={styles.footer}>
          Sign in with your Nitrex HTS account to continue
        </Text>
      </Card>
    </div>
  );
}
